/**
 * Build a renderable `Graph` from raw entity arrays.
 *
 * Two modes:
 *   - 'position-mode' (default): nodes = positions, edges = moves,
 *     plus mid-edge interception nodes for counters.
 *   - 'move-mode' (inversion): nodes = moves, edges = "this move can follow that one"
 *     (i.e., A.to === B.from).
 *
 * See docs/DECISIONS.md ADR-003 and ADR-005.
 */
import type {
  Counter,
  Graph,
  GraphEdge,
  GraphNode,
  Move,
  Position,
  Zone,
} from '../types/domain';
import { computeCentrality } from './centrality';

export type GraphMode = 'position' | 'move';

export type BuildOptions = {
  showInterceptions?: boolean;
  /**
   * Reference position set used for centrality (degree / hub / orphan)
   * computation in position-mode. Defaults to the visible `positions`.
   *
   * When the rendered set is a cluster-filtered subgraph (e.g. only
   * positions whose family is set), the surviving nodes would compute
   * their degree against the tiny subgraph and end up uniformly tiny
   * — losing the visual hierarchy that comes from "this position has
   * a lot of moves attached." Passing the broader pre-cluster-filter
   * set here keeps each node's size driven by its TRUE connectivity
   * even when most of its neighbors aren't on screen.
   */
  referencePositions?: Position[];
  /** Same idea as referencePositions but for move-mode degree. */
  referenceMoves?: Move[];
};

export function buildGraph(
  positions: Position[],
  moves: Move[],
  counters: Counter[],
  mode: GraphMode = 'position',
  options: BuildOptions = {},
): Graph {
  if (mode === 'position') return buildPositionGraph(positions, moves, counters, options);
  return buildMoveGraph(positions, moves, counters, options);
}

function buildPositionGraph(
  positions: Position[],
  moves: Move[],
  counters: Counter[],
  options: BuildOptions = {},
): Graph {
  const showInterceptions = options.showInterceptions !== false;
  // Centrality runs over the reference set so degree/hub/orphan flags
  // reflect the un-cluster-filtered graph topology. The rendered
  // graph still only emits nodes for the visible (filtered) set.
  const refPositions = options.referencePositions ?? positions;
  const refMoves = options.referenceMoves ?? moves;
  const metrics = computeCentrality(refPositions, refMoves, counters);
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const adjacency = new Map<string, string[]>();

  for (const p of positions) {
    nodes.push({
      id: p.id,
      kind: 'position',
      label: p.name,
      zone: p.zone,
      degree: metrics.degree.get(p.id) ?? 0,
      weight: metrics.weight.get(p.id) ?? 0,
      bridgeScore: metrics.bridgeScore.get(p.id) ?? 0,
      isHub: metrics.isHub.get(p.id) ?? false,
      isBridge: metrics.isBridge.get(p.id) ?? false,
      isOrphan: metrics.isOrphan.get(p.id) ?? false,
    });
    adjacency.set(p.id, []);
  }

  const counterById = new Map(counters.map(c => [c.id, c]));

  // ----- Sleeve grouping -----
  // Group moves by (source, target). Each group becomes ONE GraphEdge —
  // a "sleeve" — with moveIds listing every move in the group preserved
  // in their original order. The first move in the list is the default
  // (renders alone when the sleeve is collapsed; surfaces in the drawer
  // when the sleeve arc is clicked).
  /**
   * SELF-LOOPS ARE NOT DRAWN.
   *
   * 25 rows go from a position back to itself, all flagged
   * `noPositionalChange` — a sprawl, a down block, a collar tie escape.
   * They are true: you defend the shot and you are still standing. But an
   * arc needs two endpoints, and with one it collapses into a stub
   * hanging off the node, which reads as a rendering fault rather than as
   * information.
   *
   * Excluded from the CANVAS only, not from the data. The moves still
   * appear in the drawer's move tables, in search and in the list view —
   * which is where "what can I do from standing" actually gets read.
   * Deleting them would have been the tidier fix and the wrong one: four
   * of these techniques exist nowhere else in the map, and one of them,
   * Collar Tie Escape, carries 395 clips. Losing a well-evidenced
   * technique so that a line renders is a bad trade.
   *
   * 17 of the 25 sit on `standing`, which is why that node was the one
   * visibly sprouting stubs.
   */
  const sleeveMap = new Map<string, Move[]>();
  for (const m of moves) {
    if (m.from === m.to) continue;
    const key = `${m.from}${m.to}`;
    const arr = sleeveMap.get(key);
    if (arr) arr.push(m);
    else sleeveMap.set(key, [m]);
  }

  for (const [, group] of sleeveMap) {
    const defaultMove = group[0];
    const moveIds = group.map((mv) => mv.id);
    // Sleeve weight = mean popularity of contained moves (so cluttered
    // sleeves don't fade out just because individual moves are quieter).
    const meanWeight =
      group.reduce((acc, mv) => acc + (mv.popularity ?? 0.5), 0) / group.length;
    // Bidirectional pair? A→B and B→A are separate sleeves, but
    // bezierControl() is symmetric in its endpoints, so both would land
    // on the IDENTICAL curve — visually one line, with only the topmost
    // clickable. Tag each side with an opposite sign so the renderers bow
    // them into two lanes. Sign derives from id order so both directions
    // agree without coordinating.
    const hasReverse = sleeveMap.has(
      `${defaultMove.to}${defaultMove.from}`,
    );
    const dirSign = hasReverse ? (defaultMove.from < defaultMove.to ? 1 : -1) : 0;
    edges.push({
      id: `sleeve:${defaultMove.from}->${defaultMove.to}`,
      source: defaultMove.from,
      target: defaultMove.to,
      kind: 'move',
      moveId: defaultMove.id,
      moveIds,
      dirSign,
      weight: meanWeight,
    });
    adjacency.get(defaultMove.from)?.push(defaultMove.to);
    adjacency.get(defaultMove.to)?.push(defaultMove.from);
  }

  for (const m of moves) {
    if (!showInterceptions) continue;
    const cs = m.counters ?? [];
    if (cs.length === 0) continue;

    // For each counter, an interception node sitting on the main arc and a
    // single fork-edge from it to the counter result. Multiple counters on
    // the same move get spread along the arc at jittered t-values.
    cs.forEach((cid, idx) => {
      const c = counterById.get(cid);
      if (!c) return;
      const interceptId = `intercept:${m.id}:${cid}`;
      // Evenly spread along the middle 25% of the arc when there are many.
      const tOffset = cs.length > 1 ? (idx - (cs.length - 1) / 2) * 0.12 : 0;
      const bezierT = Math.min(0.7, Math.max(0.3, 0.5 + tOffset));

      nodes.push({
        id: interceptId,
        kind: 'interception',
        label: c.name,
        degree: 2,
        weight: typeof c.importance === 'number' ? c.importance : 0.25,
        bridgeScore: 0,
        isHub: false,
        isBridge: false,
        isOrphan: false,
        parentMoveId: m.id,
        bezierT,
      });
      adjacency.set(interceptId, []);

      edges.push({
        id: `counter-fork:${m.id}:${cid}`,
        source: interceptId,
        target: c.result,
        kind: 'counter-fork',
        moveId: m.id,
        counterId: cid,
        weight: 0.55,
      });
      adjacency.get(c.result)?.push(interceptId);
      adjacency.get(interceptId)?.push(c.result);
    });
  }

  const byId = new Map(nodes.map(n => [n.id, n]));
  const zoneCentroid: Partial<Record<Zone, [number, number, number]>> = {};

  return { nodes, edges, byId, adjacency, zoneCentroid };
}

function buildMoveGraph(
  positions: Position[],
  moves: Move[],
  counters: Counter[],
  options: BuildOptions = {},
): Graph {
  const posZone = new Map(positions.map(p => [p.id, p.zone]));
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const adjacency = new Map<string, string[]>();

  const movesByFrom = new Map<string, Move[]>();
  for (const m of moves) {
    if (!movesByFrom.has(m.from)) movesByFrom.set(m.from, []);
    movesByFrom.get(m.from)!.push(m);
  }

  for (const m of moves) {
    nodes.push({
      id: m.id,
      kind: 'move',
      label: m.name,
      zone: posZone.get(m.from),
      degree: 0,
      weight: typeof m.importance === 'number' ? m.importance : 0,
      bridgeScore: 0,
      isHub: false,
      isBridge: false,
      isOrphan: false,
    });
    adjacency.set(m.id, []);
  }

  // Edge: A.to === B.from
  for (const a of moves) {
    const followers = movesByFrom.get(a.to) ?? [];
    for (const b of followers) {
      // Same problem as the sleeve self-loops, one level up. A move whose
      // `to` equals its `from` — a sprawl, a down block — appears in its
      // OWN follower list, so it chains to itself and renders as a stub
      // on its own node. The move stays in the data; it just has no arc
      // to draw to itself.
      if (a.id === b.id) continue;
      const id = `chain:${a.id}->${b.id}`;
      edges.push({
        id,
        source: a.id,
        target: b.id,
        kind: 'inversion-chain',
        weight: 0.5,
      });
      adjacency.get(a.id)?.push(b.id);
      adjacency.get(b.id)?.push(a.id);
    }
  }

  // ---- Degree computation runs over the REFERENCE move set ----
  // When the rendered `moves` is a cluster-filtered subgraph (e.g.
  // submission-type pinned to ~70 of 303 moves), counting edges only
  // within the surviving set collapses every survivor's degree to a
  // tiny number and they all render at the base node radius. The
  // reference set defaults to `moves` (no-op for unfiltered views),
  // but Scene passes the pre-cluster-filter set so a node's size
  // still reflects its true place in the topology even when most of
  // its inversion-chain neighbours aren't on screen.
  const refMoves = options.referenceMoves ?? moves;
  const refMovesByFrom = new Map<string, Move[]>();
  for (const m of refMoves) {
    if (!refMovesByFrom.has(m.from)) refMovesByFrom.set(m.from, []);
    refMovesByFrom.get(m.from)!.push(m);
  }
  const deg = new Map<string, number>();
  for (const m of refMoves) deg.set(m.id, 0);
  for (const a of refMoves) {
    const followers = refMovesByFrom.get(a.to) ?? [];
    for (const b of followers) {
      deg.set(a.id, (deg.get(a.id) ?? 0) + 1);
      deg.set(b.id, (deg.get(b.id) ?? 0) + 1);
    }
  }
  const sorted = [...deg.values()].sort((a, b) => a - b);
  const q3 = sorted[Math.floor(sorted.length * 0.75)] ?? 0;
  const maxDeg = Math.max(1, ...deg.values());
  // Rank every distinct degree once so each node can be told where it sits
  // in the distribution rather than only what its raw count is. Ties share
  // a percentile, which is what you want: two nodes with 8 connections
  // should be the same size.
  const uniq = [...new Set(sorted)];
  const pctOf = new Map(uniq.map((d, i) => [d, uniq.length < 2 ? 1 : i / (uniq.length - 1)]));
  // Log of the degree, normalised against the busiest node. Keeps the
  // degree-proportional spacing a log curve gives while pinning the top
  // of the range, so the sizing does not silently rescale every time the
  // dataset grows — which is exactly what happened between the 62-node
  // map and this one.
  const logMax = Math.log1p(maxDeg) || 1;
  for (const n of nodes) {
    n.degree = deg.get(n.id) ?? 0;
    n.degreePercentile = pctOf.get(n.degree) ?? 0;
    n.degreeNorm = Math.log1p(n.degree) / logMax;
    // If a move declared an explicit importance, keep it; otherwise derive
    // from normalized degree.
    if (!n.weight || n.weight === 0) n.weight = n.degree / maxDeg;
    n.isHub = n.degree >= q3 && n.degree > 1;
    // Truly disconnected only; degree-1 stays a "real" node so the
    // renderer doesn't dim/shrink it as orphan-styled.
    n.isOrphan = n.degree === 0;
  }

  const byId = new Map(nodes.map(n => [n.id, n]));
  void counters; // unused in move-mode
  const zoneCentroid: Partial<Record<Zone, [number, number, number]>> = {};
  return { nodes, edges, byId, adjacency, zoneCentroid };
}
